// Libs
import { put, all, call, take, delay, select } from 'redux-saga/effects';

// Actions
import {
  SetReorderProcessing,
  SetOrderConfirmation,
} from '../../../actions/orders';
import { OpenModal, CloseModal } from '../../../actions/modal';
import { AddToCart, SetCount } from '../../../actions/cart';

import OrderConfirmation from '../../../components/OrderHistory/Modals/OrderConfirmation/OrderConfirmation';

// Store
import { store } from '../../../store';

// Utils
import { splitOrderItems } from '../../Kits/utils/sharedKitOrderItems';

// Selectors
import { sharedKitReorderMessageSelector } from '../../../store/selectors';

// Constants
import {
  PRODUCT_OUT_OF_STACK,
  PRODUCTS_OUT_OF_STACK,
  REORDER_ENTITY_WITHOUT_PRODUCT_MESSAGE,
  REORDER_ENTITY_WITHOUT_PRODUCTS_MESSAGE,
} from '../../../constants/common';

const REORDER_PROCESSING_DELAY_MS = 500;

function* reorderSaga({ payload }) {
  try {
    const { order, cancelReorder, confirmReorder, navigateToCart } = payload;
    const allProducts = store.getState().products?.all?.obj;

    const { sharedKitOnly, unavailable: unavailableProducts } = splitOrderItems(
      order.order_items,
      allProducts,
    );

    if (sharedKitOnly.length > 0) {
      const sharedKitMessage = yield select(sharedKitReorderMessageSelector);
      const confirmation = yield call(
        waitForUserConfirmation,
        sharedKitOnly.map((item) => allProducts[item.product]),
        cancelReorder,
        confirmReorder,
        sharedKitMessage,
      );

      if (!confirmation) {
        yield put(CloseModal());
        return;
      }
    }

    if (unavailableProducts.length > 0) {
      const confirmation = yield call(
        waitForUserConfirmation,
        unavailableProducts.map((item) => allProducts[item.product]),
        cancelReorder,
        confirmReorder,
      );

      if (!confirmation) {
        yield put(CloseModal());
        return;
      }
    }

    yield put(SetReorderProcessing(true));
    const unavailableProductsIds = [
      ...sharedKitOnly,
      ...unavailableProducts,
    ].map(({ product }) => product);
    const newCartItems = yield call(
      getNewCartItems,
      order,
      unavailableProductsIds,
    );

    const cartItems = store.getState().cart?.forOrder || [];

    yield all(
      newCartItems.map((newItem) => {
        const itemAlreadyInCart = cartItems.find(
          (item) => item.product === newItem.product,
        );

        if (itemAlreadyInCart) {
          const nums = itemAlreadyInCart.nums + newItem.nums;
          return put(SetCount({ ...itemAlreadyInCart, nums }));
        } else {
          return put(AddToCart(newItem));
        }
      }),
    );

    yield delay(REORDER_PROCESSING_DELAY_MS);

    yield put(SetReorderProcessing(false));

    yield put(CloseModal());

    yield call(navigateToCart);
  } catch (e) {
    console.log(e);
  }
}

export function* getNewCartItems(order, unavailableProductsIds) {
  const newCartItems = order.order_items
    .filter((item) => !unavailableProductsIds.includes(item.product))
    .map((item) => ({ product: item.product, nums: item.qty_ordered }));

  return newCartItems;
}

export function* waitForUserConfirmation(
  unavailableProducts,
  cancelReorder,
  confirmReorder,
  sharedKitMessage,
) {
  yield put(
    OpenModal({
      Component: OrderConfirmation,
      props: {
        unavailableProducts,
        header:
          sharedKitMessage ||
          (unavailableProducts.length === 1
            ? PRODUCT_OUT_OF_STACK
            : PRODUCTS_OUT_OF_STACK),
        description:
          unavailableProducts.length === 1
            ? REORDER_ENTITY_WITHOUT_PRODUCT_MESSAGE('Order')
            : REORDER_ENTITY_WITHOUT_PRODUCTS_MESSAGE('Order'),
        cancelAction: cancelReorder,
        confirmAction: confirmReorder,
      },
      styles: {
        width: 'min(90vw, 651px)',
      },
    }),
  );

  const { payload: confirmation } = yield take(SetOrderConfirmation);

  return confirmation;
}

export default reorderSaga;
